import {
  CSSProperties,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { CircularProgress } from '@mui/material';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import type {
  AiChatAssistantMessage,
  AiChatMessage,
} from '@/components/@IntranetLibrary/AiChatView';
import StreamingMarkdown from '@/components/@IntranetLibrary/AiChatView/StreamingMarkdown';
import {
  metaOf,
  splitDecisionConfirm,
  splitShareRequest,
} from '@/utils/approval/approvalChat';
import {
  approvalAiFontLevelAtom,
  approvalAiSharedMessagesAtom,
} from '../atoms';
import ShareCard from './ShareCard';
import SparkleIcon from './SparkleIcon';
import SuggestionCard from './SuggestionCard';
import {
  AiBubble,
  AiGroup,
  Body,
  Chip,
  Chips,
  CloseButton,
  DoneBadge,
  FontSizeButton,
  Footer,
  Header,
  HeaderActions,
  HeaderAvatar,
  HeaderBeta,
  HeaderText,
  HeaderTitle,
  Input,
  InputRow,
  InitLoading,
  Intro,
  LoadingMore,
  Root,
  SendButton,
  Typing,
  UserBubble,
} from './styles';

/** 글자 크기 단계 — 헤더 「가」 버튼으로 순환 (PORTAL-15728) */
const FONT_SCALES = [1, 1.15, 1.3] as const;
const FONT_LEVEL_LABELS = ['기본', '크게', '아주 크게'] as const;

export type ChatViewProps = {
  isMobile: boolean;
  messages: AiChatMessage[];
  isSending: boolean;
  isInitializing: boolean;
  suggestions: string[];
  onSend: (text: string) => void;
  onClose: () => void;
  loadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  /** 반려 제안 카드 */
  canDecide: boolean;
  decidingId: string | null;
  /** viaChat: 채팅 답("ㅇㅇ")을 AI 가 동의로 알아들어 실행 — 「네, 반려 처리해줘」 말풍선을 따로 붙이지 않는다 */
  onAccept: (
    m: AiChatAssistantMessage,
    options?: { viaChat?: boolean },
  ) => void;
  onDecline: (m: AiChatAssistantMessage) => void;
  /** 전달 카드 — 받는 사람을 참조인으로 추가 (PORTAL-15736) */
  documentId: string;
  canShare: boolean;
  onShared?: () => void | Promise<void>;
};

/**
 * 결재 AI 어시스턴트 채팅 화면 — 디자인 목업(결재문서 상세 standalone)과 같은 모양.
 * 헤더(그라데이션·아바타·닫기) / 본문(인사말·말풍선·제안 카드) / 하단(추천 질문 칩·입력·전송).
 * 공용 AiChatView 와 달리 결재 전용 룩이라 따로 둔다. 답변 마크다운 렌더러만 공용 것을 쓴다.
 */
const ChatView = ({
  isMobile,
  messages,
  isSending,
  isInitializing,
  suggestions,
  onSend,
  onClose,
  loadMore,
  hasMore,
  isLoadingMore,
  canDecide,
  decidingId,
  onAccept,
  onDecline,
  documentId,
  canShare,
  onShared,
}: ChatViewProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sharedMessages, setSharedMessages] = useAtom(
    approvalAiSharedMessagesAtom,
  );
  // 답변별 「전달」 실행 함수 — 같은 답변의 승인·반려를 누르면 전달부터 처리한다
  const shareRunsRef = useRef(new Map<string, () => Promise<boolean>>());
  const [shareReady, setShareReady] = useState<Record<string, boolean>>({});
  const handleShareReady = useCallback(
    (messageId: string, run: (() => Promise<boolean>) | null) => {
      if (run) shareRunsRef.current.set(messageId, run);
      else shareRunsRef.current.delete(messageId);
      setShareReady((prev) =>
        prev[messageId] === Boolean(run)
          ? prev
          : { ...prev, [messageId]: Boolean(run) },
      );
    },
    [],
  );
  const markShared = useCallback(
    (messageId: string) => {
      setSharedMessages((prev) => ({ ...prev, [messageId]: Date.now() }));
      void onShared?.();
    },
    [onShared, setSharedMessages],
  );
  const acceptWithShare = useCallback(
    async (m: AiChatAssistantMessage, options?: { viaChat?: boolean }) => {
      const run = shareRunsRef.current.get(m.id);
      if (run && !(await run())) return;
      onAccept(m, options);
    },
    [onAccept],
  );

  /**
   * 카드에 버튼 대신 채팅으로 답한 경우("ㅇㅇ", "진행해") — AI 가 동의로 알아들으면 답변에 실행 블록을 붙인다.
   * 막 도착한 답변(isAnimated)에만 반응하고, 바로 앞 AI 답변의 열린 카드를 「네」를 누른 것처럼 실행한다.
   * 이력으로 복원된 답변은 다시 실행하지 않는다. 결재 차례·상태는 서버가 다시 검사한다 (PORTAL-15726).
   */
  const executedDecisionsRef = useRef(new Set<string>());
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (
      !last ||
      last.kind !== 'assistant' ||
      last.status !== 'done' ||
      !last.isAnimated ||
      executedDecisionsRef.current.has(last.id)
    ) {
      return;
    }
    const { decision } = splitDecisionConfirm(
      splitShareRequest(last.text).body,
    );
    if (!decision) return;
    executedDecisionsRef.current.add(last.id);
    const prev = [...messages.slice(0, -1)]
      .reverse()
      .find((m): m is AiChatAssistantMessage => m.kind === 'assistant');
    if (!prev) return;
    if (decision.action === 'share') {
      void shareRunsRef.current.get(prev.id)?.();
      return;
    }
    const meta = metaOf(prev);
    if (
      canDecide &&
      meta?.suggestion &&
      meta.suggestion.action === decision.action &&
      (meta.suggestionState ?? 'open') === 'open'
    ) {
      void acceptWithShare(prev, { viaChat: true });
    }
  }, [acceptWithShare, canDecide, messages]);
  const [storedFontLevel, setFontLevel] = useAtom(approvalAiFontLevelAtom);
  const fontLevel =
    Number.isInteger(storedFontLevel) &&
    storedFontLevel >= 0 &&
    storedFontLevel < FONT_SCALES.length
      ? storedFontLevel
      : 0;
  const prevScrollHeightRef = useRef<number | null>(null);

  // 메시지가 늘면 아래로, 과거 로드(prepend)면 보던 위치 유지
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (prevScrollHeightRef.current != null) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // 모바일 키보드가 열리고 닫히며 보이는 영역이 바뀌면 마지막 대화가 입력창 바로 위에 오도록 맨 아래로
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) {
      return undefined;
    }
    const vv = window.visualViewport;
    vv.addEventListener('resize', scrollToBottom);
    return () => vv.removeEventListener('resize', scrollToBottom);
  }, [isMobile, scrollToBottom]);

  const scrollToBottomIfNear = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el || !hasMore || isLoadingMore) return;
    if (el.scrollTop <= 40) {
      prevScrollHeightRef.current = el.scrollHeight;
      loadMore();
    }
  };

  const send = (raw: string) => {
    const value = raw.trim();
    if (!value || isSending || isInitializing) return;
    onSend(value);
    setText('');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(text);
    }
  };

  const handleChip = (label: string) => {
    send(label);
  };

  const inputDisabled = isSending || isInitializing;

  return (
    <Root style={{ '--aichat-fs': FONT_SCALES[fontLevel] } as CSSProperties}>
      <Header isMobile={isMobile}>
        <HeaderTitle>
          <HeaderAvatar>
            <SparkleIcon />
          </HeaderAvatar>
          <HeaderText isMobile={isMobile}>
            {t('AI 어시스턴트')} <HeaderBeta>(beta)</HeaderBeta>
          </HeaderText>
        </HeaderTitle>
        <HeaderActions>
          <FontSizeButton
            type="button"
            aria-label={`${t('글자 크기')}: ${t(FONT_LEVEL_LABELS[fontLevel])}`}
            title={`${t('글자 크기')}: ${t(FONT_LEVEL_LABELS[fontLevel])}`}
            onClick={() => setFontLevel((fontLevel + 1) % FONT_SCALES.length)}
          >
            가{fontLevel > 0 && <sup>{'+'.repeat(fontLevel)}</sup>}
          </FontSizeButton>
          <CloseButton type="button" aria-label={t('닫기')} onClick={onClose}>
            ×
          </CloseButton>
        </HeaderActions>
      </Header>

      <Body ref={bodyRef} onScroll={handleScroll} isMobile={isMobile}>
        {isLoadingMore && (
          <LoadingMore>{t('이전 대화를 불러오는 중…')}</LoadingMore>
        )}
        {!isInitializing && (
          <Intro>
            {t('이 결재 문서에 대해 무엇이든 물어보세요.')}{' '}
            {t('아래 질문으로 바로 시작할 수 있습니다.')}
          </Intro>
        )}
        {/* 패널을 열 때 최근 세션 이력을 복원하는 동안 — 이력이 갑자기 나타나지 않게 가운데 스피너를 보여 준다 */}
        {isInitializing && (
          <InitLoading>
            <CircularProgress size={24} color="inherit" />
          </InitLoading>
        )}
        {messages.map((m) => {
          if (m.kind === 'user') {
            return <UserBubble key={m.id}>{m.text}</UserBubble>;
          }
          if (m.kind !== 'assistant') return null;
          const meta = metaOf(m);
          const { body: withoutShare, share } =
            m.status === 'done'
              ? splitShareRequest(m.text)
              : { body: m.text, share: null };
          const { body } = splitDecisionConfirm(withoutShare);
          return (
            <AiGroup key={m.id}>
              {m.status === 'pending' && (
                <AiBubble>
                  <Typing>
                    {t('답변을 생성하고 있어요')}
                    <i />
                    <i />
                    <i />
                  </Typing>
                </AiBubble>
              )}
              {m.status === 'error' && (
                <AiBubble isError>
                  {t('답변 생성에 실패했어요. 다시 시도해 주세요.')}
                </AiBubble>
              )}
              {m.status === 'done' && (
                <AiBubble>
                  <StreamingMarkdown
                    text={body}
                    animate={Boolean(m.isAnimated)}
                    onProgress={scrollToBottomIfNear}
                  />
                </AiBubble>
              )}
              {meta?.doneLabel && <DoneBadge>✓ {meta.doneLabel}</DoneBadge>}
              {/* 「○○에게 전달해」 — 받는 사람을 참조인으로 추가하는 확인 카드. 결재 카드보다 먼저 보인다 */}
              {share && (
                <ShareCard
                  messageId={m.id}
                  documentId={documentId}
                  share={share}
                  canShare={canShare}
                  done={Boolean(sharedMessages[m.id])}
                  onDone={() => markShared(m.id)}
                  onReadyChange={handleShareReady}
                />
              )}
              {/* 제안 카드는 지금 결재할 수 있는 사람에게만 — 결재 차례가 아닌 열람자에게 「처리할까요?」를 묻지 않는다.
                  이미 응답한 카드(처리됨)는 문서가 끝난 뒤에도 이력으로 보여 준다 */}
              {m.status === 'done' &&
                meta?.suggestion &&
                (canDecide || (meta.suggestionState ?? 'open') !== 'open') && (
                  <SuggestionCard
                    suggestion={meta.suggestion}
                    state={meta.suggestionState ?? 'open'}
                    resolvedQuietly={Boolean(meta.suggestionResolvedLocally)}
                    canDecide={canDecide}
                    submitting={decidingId === m.id}
                    note={
                      shareReady[m.id]
                        ? t('확인하면 위 전달을 먼저 처리한 뒤 진행합니다.')
                        : undefined
                    }
                    onAccept={() => void acceptWithShare(m)}
                    onDecline={() => onDecline(m)}
                  />
                )}
            </AiGroup>
          );
        })}
      </Body>

      <Footer isMobile={isMobile}>
        <Chips>
          {suggestions.map((label) => (
            <Chip
              key={label}
              type="button"
              disabled={inputDisabled}
              onClick={() => handleChip(label)}
            >
              {label}
            </Chip>
          ))}
        </Chips>
        <InputRow onSubmit={handleSubmit}>
          <Input
            type="text"
            value={text}
            placeholder={t('질문을 입력하세요')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            // 키보드가 올라오는 동안 화면이 움직이므로 조금 뒤에 맨 아래로
            onFocus={() => window.setTimeout(scrollToBottom, 300)}
            disabled={isInitializing}
          />
          <SendButton type="submit" disabled={inputDisabled || !text.trim()}>
            {t('전송')}
          </SendButton>
        </InputRow>
      </Footer>
    </Root>
  );
};

export default ChatView;
